import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { usePersistedState } from "@/hooks/usePersistedState";
import type { CompanySettings } from "@/hooks/queries";

/**
 * MS559: first-time "get started" nudge for the budget feature.
 *
 * Wireframes §8. A brand-new company (pool_cadence === null) sees no
 * pool-status widget on the Dashboard — by design, a degenerate $0/$0
 * widget is worse than none. But that leaves the budget feature
 * undiscoverable from the buyer's main surface. This card fills the
 * gap: a light, dismissible nudge that points at Settings.
 *
 * Deliberately quiet per Lembra brand (warm confidence, no urgency
 * theatre):
 *   - No exclamation marks, no "!" badge, no coral alarm treatment
 *     (that palette is reserved for the AutoPilot-no-card warning and
 *     the burn-rate critical banner — genuine action-required states).
 *   - Same white card shell + navy text as the pool widget, so when
 *     the buyer configures a budget the nudge is replaced by the
 *     widget in the same slot with no visual jolt.
 *   - Framed as an offer ("Set a team budget to track spend"), not a
 *     demand. Setting up a budget is optional — the platform works
 *     fully without one.
 *
 * Dismissal persists in localStorage (per wireframes §8 dismiss-flag;
 * scope-capped to no schema/profile changes for MS559, so localStorage
 * rather than a profiles.dismissed_prompts column). Once dismissed the
 * nudge never re-nags on this browser. The Settings nav + the Settings
 * Budget empty-state remain as the always-available discovery paths.
 *
 * Hide conditions (mirrors DashboardPoolWidget's hide-logic so the two
 * are mutually exclusive — the buyer never sees both at once):
 *   - pool_cadence is any non-null value (configured — the widget owns
 *     the slot, or the buyer chose per_celebration and wants nothing).
 *   - the buyer has dismissed the nudge.
 */

const DISMISS_KEY = "lembra:budget-setup-dismissed";

interface DashboardBudgetNudgeProps {
  /** The company's pool_cadence from useCompanySettings. The nudge
   *  renders ONLY when this is exactly null (never configured). Any
   *  pooled value or per_celebration means the buyer has made a
   *  choice; we don't second-guess it. undefined (still loading) also
   *  hides — we wait for the real value rather than flash the nudge. */
  poolCadence: CompanySettings["pool_cadence"] | undefined;
}

export function DashboardBudgetNudge({
  poolCadence,
}: DashboardBudgetNudgeProps) {
  const [dismissed, setDismissed] = usePersistedState<boolean>(
    DISMISS_KEY,
    false,
    { validate: (v): v is boolean => typeof v === "boolean" },
  );

  // Only the never-configured state (null) gets the nudge. undefined
  // (loading) and any concrete cadence both hide it.
  if (poolCadence !== null) return null;
  if (dismissed) return null;

  return (
    <div
      className="mx-8 mb-4 flex items-start justify-between gap-3 rounded-lg border bg-white px-4 py-3"
      style={{ borderColor: "rgba(29,52,97,0.08)" }}
      data-testid="dashboard-budget-nudge"
    >
      <div className="min-w-0 flex-1 text-[12.5px] leading-[1.5]" style={{ color: "rgba(29,52,97,0.75)" }}>
        Set a shared budget to pool spend across celebrations and keep an eye on
        the pace.{" "}
        <Link
          to="/settings/program#budget"
          className="font-semibold underline underline-offset-2"
          style={{ color: "#C4564A" }}
          data-testid="dashboard-budget-nudge-link"
        >
          Open Settings
        </Link>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss budget setup prompt"
        className="flex-shrink-0 rounded p-0.5 transition-colors hover:bg-[rgba(29,52,97,0.05)]"
        style={{ color: "rgba(29,52,97,0.4)" }}
        data-testid="dashboard-budget-nudge-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
